type RetryOptions = {
  retries?: number;      // total attempts = retries + 1
  delayMs?: number;      // base delay between retries
  factor?: number;       // backoff multiplier
  jitter?: number;       // 0..1 randomization
  shouldRetry?: (_err: unknown, _attempt: number) => boolean | Promise<boolean>;
  onRetry?: (_err: unknown, _attempt: number) => void | Promise<void>;
  signal?: AbortSignal;
};

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });

export const useRetryFunction = async <T>(
  fn: () => T | Promise<T>,
  opts: RetryOptions = {}
): Promise<T> => {
  const {
    retries = 3,
    delayMs = 0,
    factor = 2,
    jitter = 0,
    shouldRetry,
    onRetry,
    signal,
  } = opts;

  let attempt = 0;
  // attempt: 0..retries  (max tries = retries + 1)
  while (true) {
    try {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      return await fn();
    } catch (err) {
      const isLast = attempt >= retries;
      const ok =
        !isLast &&
        (shouldRetry ? await shouldRetry(err, attempt) : true);

      if (!ok) throw err;

      if (onRetry) await onRetry(err, attempt);

      if (delayMs > 0) {
        const backoff = delayMs * Math.pow(factor, attempt);
        const jitterMs = jitter > 0 ? backoff * jitter * Math.random() : 0;
        await sleep(backoff + jitterMs, signal);
      }

      attempt++;
    }
  }
};
